import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import toast from 'react-hot-toast';
import api from '../services/api';

interface ReferralStats {
  code: string;
  link: string;
  bonusDays: number;
  total: number;
  activated: number;
  referrals: { email: string; isVerified: boolean; createdAt: string }[];
}

function StatCard({ value, label }: { value: string | number; label: string }) {
  return (
    <Card>
      <CardContent>
        <Typography variant="h4">{value}</Typography>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </CardContent>
    </Card>
  );
}

export default function ReferralPage() {
  const [stats, setStats] = useState<ReferralStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get('/referral')
      .then((res) => setStats(res.data.data))
      .finally(() => setLoading(false));
  }, []);

  const copy = (text: string) => {
    navigator.clipboard.writeText(text).then(
      () => toast.success('Скопировано'),
      () => toast.error('Не удалось скопировать')
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (!stats) return null;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Реферальная программа
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <CardGiftcardIcon color="primary" />
            <Typography variant="h6">
              Приглашайте друзей — получайте {stats.bonusDays} дней подписки за каждого
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Поделитесь ссылкой. Когда приглашённый зарегистрируется и подтвердит email, к вашей
            активной подписке добавится {stats.bonusDays} дней.
          </Typography>

          <TextField
            fullWidth
            label="Ваша реферальная ссылка"
            value={stats.link}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <Button startIcon={<ContentCopyIcon />} onClick={() => copy(stats.link)}>
                    Копировать
                  </Button>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Ваш код:
            </Typography>
            <Chip label={stats.code} onClick={() => copy(stats.code)} />
          </Box>
        </CardContent>
      </Card>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <StatCard value={stats.total} label="Всего приглашено" />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard value={stats.activated} label="Подтвердили email" />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard value={`${stats.activated * stats.bonusDays} дн.`} label="Бонусных дней начислено" />
        </Grid>
      </Grid>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Приглашённые
          </Typography>
          <Divider sx={{ mb: 1 }} />
          {stats.referrals.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Пока никого — поделитесь ссылкой, чтобы начать получать бонусы.
            </Typography>
          ) : (
            <List dense>
              {stats.referrals.map((r, i) => (
                <ListItem key={i} disableGutters>
                  <ListItemText
                    primary={r.email}
                    secondary={new Date(r.createdAt).toLocaleDateString('ru-RU')}
                  />
                  <Chip
                    size="small"
                    label={r.isVerified ? 'активирован' : 'ожидает'}
                    color={r.isVerified ? 'success' : 'default'}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
